import { SILENT_REDIRECT_URI, SILENT_SSO_TIMEOUT_MS } from './config';
import { oidcSession } from './oidcSession';
import { createCodeChallenge, createCodeVerifier } from './pkce';
import { buildAuthorizeUrl } from './urls';

export const SILENT_CALLBACK_MESSAGE = 'oidc:callback';

type TSilentCallbackMessage = {
	type: typeof SILENT_CALLBACK_MESSAGE;
	url: string;
};

export type TSilentAuthorization = {
	code: string;
	sessionState: string;
	codeVerifier: string | null;
	redirectUri: string;
};

const isSilentCallbackMessage = (data: unknown): data is TSilentCallbackMessage =>
	typeof data === 'object' &&
	data !== null &&
	(data as TSilentCallbackMessage).type === SILENT_CALLBACK_MESSAGE &&
	typeof (data as TSilentCallbackMessage).url === 'string';

export class SilentSsoError extends Error {
	readonly reason: string;

	constructor(reason: string) {
		super(`Silent SSO failed: ${reason}`);
		this.name = 'SilentSsoError';
		this.reason = reason;
	}
}

export const requestSilentAuthorization = async (): Promise<TSilentAuthorization> => {
	const codeVerifier = createCodeVerifier();
	const codeChallenge = await createCodeChallenge(codeVerifier);
	const state = oidcSession.beginSilent(codeVerifier);
	const src = buildAuthorizeUrl({
		state,
		redirectUri: SILENT_REDIRECT_URI,
		codeChallenge,
		silent: true,
	});

	return new Promise<TSilentAuthorization>((resolve, reject) => {
		const iframe = document.createElement('iframe');
		iframe.title = 'silent check-sso';
		iframe.setAttribute('aria-hidden', 'true');
		iframe.style.cssText =
			'position:absolute;width:0;height:0;border:0;visibility:hidden;left:-9999px;';

		let settled = false;
		let timeoutId = 0;

		const cleanup = () => {
			window.clearTimeout(timeoutId);
			window.removeEventListener('message', onMessage);
			iframe.remove();
		};

		const fail = (reason: string) => {
			if (settled) return;
			settled = true;
			cleanup();
			reject(new SilentSsoError(reason));
		};

		const succeed = (result: TSilentAuthorization) => {
			if (settled) return;
			settled = true;
			cleanup();
			resolve(result);
		};

		function onMessage(event: MessageEvent) {
			if (event.origin !== window.location.origin) return;
			if (event.source !== iframe.contentWindow) return;
			if (!isSilentCallbackMessage(event.data)) return;

			const params = new URL(event.data.url).searchParams;
			const handshake = oidcSession.consumeSilent(params.get('state'));

			if (!handshake.valid) {
				fail('state_mismatch');
				return;
			}

			const error = params.get('error');
			if (error) {
				fail(error);
				return;
			}

			const code = params.get('code');
			if (!code) {
				fail('missing_code');
				return;
			}

			succeed({
				code,
				sessionState: params.get('session_state') ?? '',
				codeVerifier: handshake.codeVerifier,
				redirectUri: SILENT_REDIRECT_URI,
			});
		}

		window.addEventListener('message', onMessage);
		timeoutId = window.setTimeout(() => {
			oidcSession.consumeSilent(null);
			fail('timeout');
		}, SILENT_SSO_TIMEOUT_MS);

		iframe.src = src;
		document.body.appendChild(iframe);
	});
};
